import dayjs from 'dayjs';
import { IComponent } from 'gcf-player/shared-utils';
import { EComponentInstanceDataTarget } from 'src/components/gcfPreview/GcfPreview.interface';
import { getOverrideDateLimits } from 'src/features/locations/scheduleComponentPage/modules/componentAccordion/modules/ComponentAccordionContent.helper';
import { ComponentTypes } from 'src/interfaces';
import { getBackgroundVideoDuration } from 'src/utils/file-types';

import {
  ISelectedSectionBackup,
  ISelectedSectionDetailInfo,
} from '../../MenuBoardModificationPage.interface';

const allowedOverrideableComponentTypes: readonly ComponentTypes[] = [
  ComponentTypes.PROMOTIONAL_SPACE,
  ComponentTypes.PROMOTIONAL_COMPONENT,
  ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS,
  ComponentTypes.LISTING_VISUALS_GALLERY,
  ComponentTypes.SINGLE_SHELF,
];

export const isComponentTypeAllowedToOverride = (
  componentType?: ComponentTypes | string,
): componentType is ComponentTypes =>
  allowedOverrideableComponentTypes.includes(componentType as ComponentTypes);

export const isAllowedToAddOverride = (
  selectedSectionDetailInfo: ISelectedSectionDetailInfo[],
): boolean => {
  const selectedScheduleItem =
    selectedSectionDetailInfo.find(
      (item) => item?.type === EComponentInstanceDataTarget.SCHEDULE_ITEM,
    ) ||
    selectedSectionDetailInfo.find(
      (item) =>
        item?.type !== EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE,
    ) ||
    selectedSectionDetailInfo[selectedSectionDetailInfo.length - 1];

  if (!selectedScheduleItem?.allowOverrides) {
    return false;
  }

  const selectedComponent = selectedScheduleItem.component as
    | (IComponent & { component_type?: { name?: ComponentTypes } })
    | undefined;
  const selectedComponentType =
    selectedComponent?.component_type?.name || selectedComponent?.type;

  return isComponentTypeAllowedToOverride(selectedComponentType);
};

export const getVideoComponentDuration = async (component: IComponent) =>
  getBackgroundVideoDuration(component?.config?.background);

export const getBackupsName = (backups: ISelectedSectionBackup[]) => {
  return backups?.map((backup) => backup?.component?.name)?.join(',');
};

export const formatVideoDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) {
    return '00:00';
  }

  const dur = dayjs.duration(seconds, 'seconds');
  const hours = dur.hours();
  const minutes = dur.minutes();
  const secs = dur.seconds();

  // Format based on duration length
  if (hours > 0) {
    return dayjs.duration({ hours, minutes, seconds: secs }).format('HH:mm:ss');
  }

  return dayjs.duration({ minutes, seconds: secs }).format('mm:ss');
};

export const getOverrideDatePickerLimits = (
  selectedScheduleItem?: ISelectedSectionDetailInfo,
  overrideItem?: ISelectedSectionDetailInfo,
) => {
  if (!selectedScheduleItem || !overrideItem?.component) {
    return {};
  }

  const { startDateMin, startDateMax, endDateMin, endDateMax } =
    getOverrideDateLimits(
      {
        scheduleStartDate: selectedScheduleItem.startDate,
        scheduleEndDate: selectedScheduleItem.endDate,
      } as any,
      overrideItem.component as any,
      overrideItem.startDate,
    );
  const today = dayjs().startOf('day');
  const maxSelectableDate = today.add(3, 'year');
  const adjustedStartDateMin =
    startDateMin && startDateMin.isAfter(today, 'day') ? startDateMin : today;
  const adjustedEndDateMax = endDateMax || maxSelectableDate;

  return {
    startDateMin: adjustedStartDateMin,
    startDateMax,
    endDateMin,
    endDateMax: adjustedEndDateMax,
  };
};
